/**
 * iQuickCalc Application Configuration
 */

import type { DropdownOption, SectionConfig } from '../shared';

export const APP_NAME = 'iQuickCalc';
export const APP_VERSION = '1.0.0';
export const APP_VERSION_POLICY = { currentVersion: APP_VERSION };

// Load type options
export const LOAD_TYPES: DropdownOption[] = [
  { value: '0', label: 'Triangular Load' },
  { value: '1', label: 'Triangular Pulse Load' },
  { value: '2', label: 'Rectangular Load' }
];

/**
 * API Payload Type
 */
export interface APIPayload {
  title: string;
  length: string;
  tribWidth: string;
  hingeLength: string;
  totalWeight: string;
  klmFactor: string;
  Ru: string;
  Xe: number;
  LoadType: string;
  Pa: number;
  td: number;
}

/**
 * API Response Type
 */
export interface APIResponse {
  Pa: number;
  Ia: number;
  td: number;
  TA: number;
  K: number;
  Me: number;
  Tn: number;
  Ru: number;
  td_over_Tn: number;
  Ru_over_Pa: number;
  duct: number;
  rot: number;
  Xmax: number;
  tmax: number;
  V: number;
  Vr?: number;
  charts: {
    load_chart?: { mime: string; base64: string };
    chart_qc?: { mime: string; base64: string };
    chart_qc_max?: { mime: string; base64: string };
    chart_rebound?: { mime: string; base64: string };
  };
}

import { createElement as h } from 'react';
/**
 * Response Display Configuration
 */

export const RESPONSE_DISPLAY_CONFIG: SectionConfig<APIResponse>[] = [
  {
    title: 'Results',
    fields: [
      { label: 'Peak Pressure', getValue: data => `${data.Pa.toFixed(2)} psi` },
      { label: 'Impulse', getValue: data => `${data.Ia.toFixed(2)} psi-ms` },
      { label: 'Duration', getValue: data => `${data.td.toFixed(2)} ms` },
      { label: 'Tributary Area', getValue: data => h('span', null, data.TA.toFixed(2), ' ft', h('sup', null, '2')) },
      { label: 'Stiffness', getValue: data => `${data.K.toFixed(2)} kip/in` },
      { label: 'Effective Mass', getValue: data => `${data.Me.toFixed(3)} lbf-sec²/in` },
      { label: 'Natural Period', getValue: data => `${data.Tn.toFixed(2)} ms` },
      { label: 'Ru', getValue: data => `${data.Ru.toFixed(2)} psi` }
    ]
  },
  {
    title: 'Other Results',
    fields: [
      { label: 'td/Tn', getValue: data => `${data.td_over_Tn.toFixed(2)}` },
      { label: 'Ru/Pa', getValue: data => `${data.Ru_over_Pa.toFixed(2)}` },
      { label: 'Ductility', getValue: data => `${data.duct.toFixed(2)}` },
      { label: 'Rotation', getValue: data => `${data.rot.toFixed(2)} deg` },
      { label: 'Max Displacement', getValue: data => `${data.Xmax.toFixed(2)} in @${data.tmax.toFixed(2)} ms ` },
      { label: 'Support Shear', getValue: data => `${data.V.toFixed(2)} kips` },
      { label: 'Rebound Shear', getValue: data => (data.Vr !== undefined ? `${data.Vr.toFixed(2)} kips` : '—') }
    ]
  }
];
